import { Trip } from '../../types/cms.types';
import { TripService, TripFilter, TripListResponse } from '../../service/supabase/TripService';
import IBloc from '../../base/IBloc';

export interface TripState {
    trips: Trip[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    loading: boolean;
    error: string | null;
    filters: TripFilter;
}

export interface TripViewState {
    trips: Trip[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    filters: TripFilter;
    hasData: boolean;
}

export class BlocManagementTrips extends IBloc {
    private tripService: TripService;
    private useMockData: boolean = false; // Use real API now
    
    /**
     * State chính của Trips Management
     */
    info: TripViewState = {
        trips: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        hasData: false
    };

    constructor() {
        super();
        this.tripService = TripService.getInstance();
    }

    /**
     * Khởi tạo dữ liệu
     */
    initData() {
        console.log('BlocManagementTrips: initData called');
        this.setStream("info", this.info);
        this.loadTrips();
    }


    /**
     * Load trips với pagination và filters
     */
    async loadTrips(page?: number, limit?: number, filters?: TripFilter): Promise<void> {
        try {
            console.log('BlocManagementTrips: loadTrips called', { page, limit, filters });
            this.info.error = null;
            this.info.loading = true;
            this.setStream("info", this.info);

            const pageToLoad = page ?? this.info.pagination.page;
            const limitToLoad = limit ?? this.info.pagination.limit;
            const filtersToApply = filters ?? this.info.filters;

            // Update filters if provided
            if (filters) {
                this.info.filters = filtersToApply;
            }

            let response: TripListResponse;
            response = await this.tripService.getTrips(
                pageToLoad,
                limitToLoad,
                filtersToApply
            );
            
            console.log('BlocManagementTrips: API response', response);

            // Update state
            this.info.trips = response.data;
            this.info.pagination = {
                total: response.total,
                page: response.page,
                limit: response.limit,
                totalPages: response.totalPages
            };
            this.info.loading = false;
            this.info.hasData = response.data.length > 0;
            
            console.log('BlocManagementTrips: Updated state', this.info);
            this.setStream("info", this.info);

        } catch (error) {
            console.error('Error loading trips:', error);
            this.info.error = error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải dữ liệu';
            this.info.trips = [];
            this.info.loading = false;
            this.info.hasData = false;
            this.setStream("info", this.info);
        }
    }



    /**
     * Search trips
     */
    async searchTrips(searchTerm: string): Promise<void> {
        const newFilters = { ...this.info.filters, search: searchTerm };
        await this.loadTrips(1, undefined, newFilters);
    }

    /**
     * Filter trips by status
     */
    async filterByStatus(status: string): Promise<void> {
        const newFilters = { ...this.info.filters, status: status || undefined };
        await this.loadTrips(1, undefined, newFilters);
    }

    /**
     * Filter trips by vehicle type
     */
    async filterByVehicleType(vehicleType: string): Promise<void> {
        const newFilters = { ...this.info.filters, vehicle_type: vehicleType || undefined };
        await this.loadTrips(1, undefined, newFilters);
    }

    /**
     * Filter trips by date range
     */
    async filterByDateRange(dateFrom?: string, dateTo?: string): Promise<void> {
        const newFilters = { 
            ...this.info.filters, 
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined
        };
        await this.loadTrips(1, undefined, newFilters);
    }

    /**
     * Clear all filters
     */
    async clearFilters(): Promise<void> {
        this.info.filters = {};
        await this.loadTrips(1);
    }

    /**
     * Change page
     */
    async changePage(page: number): Promise<void> {
        await this.loadTrips(page);
    }

    /**
     * Change page size
     */
    async changePageSize(limit: number): Promise<void> {
        await this.loadTrips(1, limit);
    }

    /**
     * Refresh data
     */
    async refresh(): Promise<void> {
        await this.loadTrips(this.info.pagination.page);
    }

    /**
     * Delete trip
     */
    async deleteTrip(tripId: string): Promise<void> {
        try {
            this.info.loading = true;
            this.info.error = null;
            this.setStream("info", this.info);

            await this.tripService.deleteTrip(tripId);
            
            // Refresh current page
            await this.refresh();
        } catch (error) {
            console.error('Error deleting trip:', error);
            this.info.error = error instanceof Error ? error.message : 'Có lỗi xảy ra khi xóa chuyến đi';
            this.info.loading = false;
            this.setStream("info", this.info);
        }
    }

    /**
     * Update trip status
     */
    async updateTripStatus(tripId: string, status: string): Promise<void> {
        try {
            this.info.loading = true;
            this.info.error = null;
            this.setStream("info", this.info);

            await this.tripService.updateTripStatus(tripId, status);
            
            // Refresh current page
            await this.refresh();
        } catch (error) {
            console.error('Error updating trip status:', error);
            this.info.error = error instanceof Error ? error.message : 'Có lỗi xảy ra khi cập nhật trạng thái';
            this.info.loading = false;
            this.setStream("info", this.info);
        }
    }

  

    /**
     * Check if using mock data
     */
    get isMockMode(): boolean {
        return this.useMockData;
    }

    /**
     * Get current state values - for backward compatibility
     */
    get currentTrips(): Trip[] {
        return this.info.trips;
    }

    get currentPagination() {
        return this.info.pagination;
    }

    get currentFilters(): TripFilter {
        return this.info.filters;
    }

    get isLoading(): boolean {
        return this.info.loading;
    }

    get currentError(): string | null {
        return this.info.error;
    }

    /**
     * Dispose of the bloc
     */
    dispose(): void {
        super.dispose();
    }
}
